/**
 * What's Cooking Sidebar
 * Shows trending data: hashtags, cuisines, venues, foodies, dishes
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, Hash, Utensils, MapPin, Users, Flame, 
  ChevronRight, Sparkles, ArrowUpRight, Activity
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsCookingSidebar = ({ className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trending');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/posts/whats-cooking`);
        if (res.ok) {
          const json = await res.json();
          setData(json);
        }
      } catch (e) {
        console.error('Failed to fetch whats-cooking:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card className={`overflow-hidden ${className}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse" />
            <div className="h-5 bg-gray-200 rounded w-32 animate-pulse" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const tabs = [
    { key: 'trending', label: 'Trending', icon: Flame },
    { key: 'hashtags', label: 'Tags', icon: Hash },
    { key: 'foodies', label: 'Foodies', icon: Users },
  ];

  return (
    <Card className={`overflow-hidden border-0 shadow-lg bg-gradient-to-br from-white to-orange-50/30 ${className}`} data-testid="whats-cooking-sidebar">
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-4 pb-3 bg-gradient-to-r from-[#F4C430]/10 to-[#FF6B35]/10 border-b border-orange-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-[#F4C430] to-[#FF6B35] rounded-xl flex items-center justify-center shadow-md">
                <Flame className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm">What's Cooking</h3>
                <p className="text-[10px] text-gray-500">Trending this week</p>
              </div>
            </div>
            {data?.stats && (
              <div className="flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                <Activity className="w-3 h-3" />
                {data.stats.posts_today} today
              </div>
            )}
          </div>
          
          {/* Mini Tabs */}
          <div className="flex gap-1 mt-3">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  activeTab === tab.key 
                    ? 'bg-white shadow-sm text-[#FF6B35]' 
                    : 'text-gray-500 hover:bg-white/50'
                }`}
              >
                <tab.icon className="w-3 h-3" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Trending Dishes Tab */}
          {activeTab === 'trending' && (
            <div className="space-y-2.5">
              {data?.trending_dishes?.length > 0 ? (
                data.trending_dishes.slice(0, 5).map((dish, idx) => (
                  <TrendingDishItem key={dish.dish} dish={dish} rank={idx + 1} />
                ))
              ) : data?.hot_venues?.length > 0 ? (
                data.hot_venues.slice(0, 5).map((venue, idx) => (
                  <HotVenueItem key={venue.name} venue={venue} rank={idx + 1} />
                ))
              ) : (
                <EmptyState message="No trending dishes yet" />
              )}
              
              {/* Popular Cuisines */}
              {data?.popular_cuisines?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Popular Cuisines</p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.popular_cuisines.map(c => (
                      <Badge 
                        key={c.cuisine} 
                        variant="secondary" 
                        className="bg-gradient-to-r from-orange-50 to-amber-50 text-orange-700 border border-orange-100 text-[10px] px-2 py-0.5"
                      >
                        <Utensils className="w-2.5 h-2.5 mr-1" />
                        {c.cuisine}
                        <span className="ml-1 text-orange-400">({c.count})</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hashtags Tab */}
          {activeTab === 'hashtags' && (
            <div className="space-y-2">
              {data?.trending_hashtags?.length > 0 ? (
                data.trending_hashtags.map((h, idx) => (
                  <HashtagItem key={h.tag} hashtag={h} rank={idx + 1} />
                ))
              ) : (
                <EmptyState message="No trending hashtags yet" />
              )}
            </div>
          )}

          {/* Foodies Tab */}
          {activeTab === 'foodies' && (
            <div className="space-y-2.5">
              {data?.active_foodies?.length > 0 ? (
                data.active_foodies.map((foodie, idx) => (
                  <FoodieItem key={foodie.user_id} foodie={foodie} rank={idx + 1} />
                ))
              ) : (
                <EmptyState message="No active foodies this week" />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 pb-4">
          <Link 
            to="/foodie"
            className="flex items-center justify-center gap-1.5 w-full py-2.5 bg-gradient-to-r from-[#F4C430]/10 to-[#FF6B35]/10 hover:from-[#F4C430]/20 hover:to-[#FF6B35]/20 rounded-xl text-sm font-medium text-[#FF6B35] transition-all"
          >
            <Sparkles className="w-4 h-4" />
            Explore All Trends
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

// ═══ Sub-components ═══

const TrendingDishItem = ({ dish, rank }) => {
  const fallbackImg = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=100&q=60';
  
  return (
    <div className="flex items-center gap-2.5 p-2 -mx-2 rounded-xl hover:bg-white hover:shadow-sm transition-all cursor-pointer group">
      <div className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
        rank === 1 ? 'bg-amber-100 text-amber-600' :
        rank === 2 ? 'bg-gray-100 text-gray-600' :
        rank === 3 ? 'bg-orange-100 text-orange-600' :
        'bg-gray-50 text-gray-400'
      }`}>
        {rank}
      </div>
      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
        <img 
          src={dish.image || fallbackImg} 
          alt={dish.dish}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = fallbackImg; }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-[#FF6B35] transition-colors">
          {dish.dish}
        </p>
        <p className="text-[10px] text-gray-400">{dish.count} posts</p>
      </div>
      <TrendingUp className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
    </div>
  );
};

const HotVenueItem = ({ venue, rank }) => {
  return (
    <div className="flex items-center gap-2.5 p-2 -mx-2 rounded-xl hover:bg-white hover:shadow-sm transition-all cursor-pointer group">
      <div className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
        rank === 1 ? 'bg-rose-100 text-rose-600' :
        rank === 2 ? 'bg-gray-100 text-gray-600' :
        'bg-gray-50 text-gray-400'
      }`}>
        {rank}
      </div>
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-100 to-orange-100 flex items-center justify-center flex-shrink-0">
        <MapPin className="w-4 h-4 text-rose-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-[#FF6B35] transition-colors">
          {venue.name}
        </p>
        <p className="text-[10px] text-gray-400">{venue.city} · {venue.posts_count} posts</p>
      </div>
      <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#FF6B35] flex-shrink-0" />
    </div>
  );
};

const HashtagItem = ({ hashtag, rank }) => {
  return (
    <div className="flex items-center gap-2.5 p-2 -mx-2 rounded-xl hover:bg-white hover:shadow-sm transition-all cursor-pointer group">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center flex-shrink-0">
        <Hash className="w-4 h-4 text-blue-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
          #{hashtag.tag}
        </p>
        <p className="text-[10px] text-gray-400">{hashtag.count} posts</p>
      </div>
      {hashtag.trend === 'up' && (
        <div className="flex items-center gap-0.5 text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
          <TrendingUp className="w-2.5 h-2.5" />
          Hot
        </div>
      )}
    </div>
  );
};

const FoodieItem = ({ foodie, rank }) => {
  return (
    <Link 
      to={`/profile/${foodie.user_id}`}
      className="flex items-center gap-2.5 p-2 -mx-2 rounded-xl hover:bg-white hover:shadow-sm transition-all cursor-pointer group"
    >
      <div className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
        rank === 1 ? 'bg-purple-100 text-purple-600' :
        rank === 2 ? 'bg-gray-100 text-gray-600' :
        'bg-gray-50 text-gray-400'
      }`}>
        {rank}
      </div>
      <Avatar className="w-9 h-9 border-2 border-white shadow-sm">
        {foodie.avatar_url ? (
          <AvatarImage src={foodie.avatar_url} alt={foodie.user_name} />
        ) : null}
        <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-xs font-bold">
          {foodie.user_name?.[0]?.toUpperCase() || 'F'}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-[#FF6B35] transition-colors">
          {foodie.user_name}
        </p>
        <p className="text-[10px] text-gray-400">{foodie.posts_count} posts this week</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF6B35] flex-shrink-0" />
    </Link>
  );
};

const EmptyState = ({ message }) => (
  <div className="text-center py-6">
    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
      <Sparkles className="w-5 h-5 text-gray-400" />
    </div>
    <p className="text-xs text-gray-500">{message}</p>
    <p className="text-[10px] text-gray-400 mt-1">Start posting to see trends!</p>
  </div>
);

export default WhatsCookingSidebar;
